import React from 'react';
import MuiFormControl from '@mui/material/FormControl';
import MuiTextField, {
  TextFieldProps as MuiTextFieldProps,
} from '@mui/material/TextField';
import MuiInputAdornment from '@mui/material/InputAdornment';
import { IconButton } from '../icon-button';
import { MaterialIcon } from '../icons';
import { SxProps, Theme } from '@mui/material';

/**
 * `TextField` component that extends MUI's `TextField` with additional features such as
 * start and end adornments, and an option to allow clearing the input field.
 *
 * @param {ExtendedMuiTextFieldProps} props - The props for the `TextField` component.
 * @returns {JSX.Element} A styled `TextField` with custom adornments and clear button functionality.
 *
 * @example
 * ```tsx
 * import { TextField } from './TextField';
 *
 * const MyComponent = () => (
 *   <TextField
 *     fullWidth
 *     label="Username"
 *     startAdornment={<SvgIcon.user />}
 *     endAdornment={<SvgIcon.lock />}
 *     allowClear
 *     onChange={(e) => console.log(e.target.value)}
 *   />
 * );
 * ```
 *
 */
export type ExtendedMuiTextFieldProps = MuiTextFieldProps & {
  /**
   * If `true`, the input will take up the full width of its container.
   */
  fullWidth?: boolean;

  /**
   * Custom content to be displayed at the start of the input field.
   */
  startAdornment?: React.ReactNode;

  /**
   * Custom content to be displayed at the end of the input field.
   */
  endAdornment?: React.ReactNode;

  /**
   * If `true`, an icon button for clearing the input will be displayed at the end.
   */
  allowClear?: boolean;

  /**
   * The system prop that allows defining system overrides as well as additional CSS styles for Select's FormControl Wrapper.
   */
  formSx?: SxProps<Theme> | undefined;
};

/**
 * A custom `TextField` component that integrates MUI's `TextField` with optional
 * start and end adornments, and a clear button functionality when `allowClear` is `true`.
 *
 * @param {ExtendedMuiTextFieldProps} props - Props passed to the `TextField` component.
 * @returns {React.ReactElement} The rendered `TextField` with optional adornments and clear button.
 *
 * @category Component
 * @document TextfieldComponentUsage.md
 */
export const TextField = ({
  fullWidth,
  startAdornment,
  endAdornment,
  allowClear,
  formSx,
  ...props
}: ExtendedMuiTextFieldProps): React.ReactElement => {
  const handleClear: React.MouseEventHandler<HTMLButtonElement> = event => {
    event.preventDefault();
    event.stopPropagation();
    props.onChange!({
      target: { value: '' },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  return (
    <MuiFormControl fullWidth={fullWidth} sx={formSx}>
      <MuiTextField
        {...props}
        slotProps={{
          input: {
            ...props?.slotProps?.input,
            startAdornment: startAdornment,
            endAdornment:
              allowClear && !endAdornment ? (
                <MuiInputAdornment position="end">
                  <IconButton onClick={handleClear}>
                    <MaterialIcon.cancel_outlined
                      sx={{
                        width: '24px',
                        height: '24px',
                        fill: theme => theme.palette.surface.onSurfaceVar,
                      }}
                    />
                  </IconButton>
                </MuiInputAdornment>
              ) : (
                endAdornment
              ),
          },
        }}
      />
    </MuiFormControl>
  );
};
